import * as ImagePicker from "expo-image-picker";
import { useQuery } from "@tanstack/react-query";

import { deleteUploadedFile, signedFileUrl, uploadFile } from "~/api/uploads";

export type MediaBucket = "item-attachments";

export type PickedImage = {
  uri: string;
  width: number;
  height: number;
  mimeType: string;
};

export async function pickImage(
  options: { allowsEditing?: boolean } = {},
): Promise<PickedImage | null> {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!permission.granted) {
    throw new Error("GearSync needs photo library access to attach images.");
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ["images"],
    allowsEditing: options.allowsEditing ?? false,
    quality: 0.85,
  });
  const asset = result.canceled ? null : result.assets[0];
  if (!asset) return null;

  return {
    uri: asset.uri,
    width: asset.width,
    height: asset.height,
    mimeType: asset.mimeType ?? "image/jpeg",
  };
}

export async function captureImage(): Promise<PickedImage | null> {
  const permission = await ImagePicker.requestCameraPermissionsAsync();
  if (!permission.granted) {
    throw new Error(
      "GearSync needs camera access to photograph receipts and gear.",
    );
  }

  const result = await ImagePicker.launchCameraAsync({ quality: 0.85 });
  const asset = result.canceled ? null : result.assets[0];
  if (!asset) return null;

  return {
    uri: asset.uri,
    width: asset.width,
    height: asset.height,
    mimeType: asset.mimeType ?? "image/jpeg",
  };
}

/** Files stay private to their owner; the API returns the stored path. */
export async function uploadImage(
  _bucket: MediaBucket,
  _userId: string,
  image: PickedImage,
) {
  const body = await fetch(image.uri).then((response) =>
    response.arrayBuffer(),
  );
  return uploadFile(body, image.mimeType);
}

export async function removeImage(_bucket: MediaBucket, path: string) {
  await deleteUploadedFile(path);
}

/** Buckets are private, so display needs a short-lived signed URL. */
export function useSignedUrl(
  bucket: MediaBucket,
  path: string | null | undefined,
) {
  return useQuery({
    queryKey: ["gearsync-signed-url", bucket, path ?? ""],
    enabled: Boolean(path),
    staleTime: 45 * 60 * 1000,
    queryFn: () => signedFileUrl(path!),
  });
}
